import ApiService from '@/core/services/ApiService';
import { Actions, Mutations } from '@/store/enums/StoreEnums';
import { Module, Action, Mutation, VuexModule } from 'vuex-module-decorators';

export interface NewsletterObject {
  id: number;
  title: string;
  key: string;
  htmlContent: string;
  variables: any;
  isEnabled: boolean;
  isActive: boolean;
  sort: number;
  version: number;
}

export interface NewsletterSubscribers {
  id: number;
  email: string;
  is_active: string;
  subscribed_at: Date;
  updated_at: Date;
}

export interface NewsletterListInfo {
  totalItems: number;
  currentPage: number;
  pageSize: number;
  isNext: boolean;
  previous: number;
  newsletters: Array<NewsletterObject>;
  errors: unknown;
}

@Module
export default class NewsletterModule
  extends VuexModule
  implements NewsletterListInfo
{
  newsletters = [] as Array<NewsletterObject>;
  allNewsletters = [] as Array<NewsletterObject>;
  newsletterSubscribers = [] as Array<NewsletterSubscribers>;

  totalItems = 0;
  currentPage = 1;
  pageSize = 10;
  previous = 0;
  isNext = true;
  errors = {};

  newsletterSubscribersTotal = 0;
  newsletterSubscribersCurrentPage = 1;
  newsletterSubscribersPageSize = 2;
  newsletterSubscribersPrevious = 0;
  newsletterSubscribersIsNext = true;

  /**
   * Get newsletters list
   * @returns Array<NewsletterObject>
   */
  get newslettersList(): Array<NewsletterObject> {
    return this.newsletters;
  }

  /**
   * Get all newsletters list
   * @returns Array<NewsletterObject>
   */
  get allListNewsletters(): Array<NewsletterObject> {
    return this.allNewsletters;
  }

  /**
   * Get total items
   * @returns number
   */
  get newslettersListCount(): number {
    return this.totalItems;
  }

  get newsletterSubscribersList(): Array<NewsletterSubscribers> {
    return this.newsletterSubscribers;
  }

  get newsletterSubscribersCount(): number {
    return this.newsletterSubscribersTotal;
  }

  @Mutation
  [Mutations.SET_NEWSLETTERS](data) {
    this.newsletters = data.data;
    this.totalItems = data.totalItems;
    this.currentPage = data.currentPage;
    this.pageSize = data.pageSize;
    this.isNext = data.isNext;
    this.previous = data.previous;
    this.errors = {};
  }

  @Mutation
  [Mutations.SET_NEWSLETTERS_CURRENT_PAGE](page) {
    this.currentPage = page;
  }

  @Mutation
  [Mutations.SET_ALL_NEWSLETTERS](data) {
    this.allNewsletters = data.data;
  }

  @Mutation
  [Mutations.RESET_NEWSLETTERS_LIST]() {
    this.newsletters = [] as Array<NewsletterObject>;
    this.totalItems = 0;
    this.currentPage = 1;
    this.pageSize = 10;
    this.previous = 0;
    this.isNext = true;
    this.errors = {};
  }

  @Mutation
  [Mutations.RESET_NEWSLETTERS_SUBSCRIBERS_LIST]() {
    this.newsletterSubscribers = [] as Array<NewsletterSubscribers>;
    this.newsletterSubscribersTotal = 0;
    this.newsletterSubscribersCurrentPage = 1;
    this.newsletterSubscribersPageSize = 2;
    this.newsletterSubscribersPrevious = 0;
    this.newsletterSubscribersIsNext = true;
    this.errors = {};
  }

  @Mutation
  [Mutations.SET_NEWSLETTER_SUBSCRIBERS_CURRENT_PAGE](page) {
    this.currentPage = page;
  }

  @Mutation
  [Mutations.SET_NEWSLETTER_SUBSCRIBERS](data) {
    this.newsletterSubscribers = data.data;
    this.newsletterSubscribersTotal = data.totalItems;
    this.errors = {};
  }

  @Mutation
  [Mutations.SET_CHARGE_LOGS_CURRENT_PAGE](page) {
    this.currentPage = page;
  }

  @Action
  [Actions.RESET_NEWSLETTERS_STORE]() {
    this.context.commit(Mutations.RESET_NEWSLETTERS_LIST);
  }

  @Action
  [Actions.CREATE_NEWSLETTER](data) {
    ApiService.setAuthorizationHeader();
    return ApiService.post('/newsletter-sections', data)
      .then(({ data }) => {
        this.context.commit(Mutations.RESET_NEWSLETTERS_LIST);
        return data.data;
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.DELETE_NEWSLETTER](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.delete('/newsletter-sections', id)
      .then(() => {
        this.context.commit(Mutations.RESET_NEWSLETTERS_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.UPDATE_NEWSLETTER]({ id, data }) {
    ApiService.setAuthorizationHeader();
    return ApiService.update('/newsletter-sections', id, data)
      .then(() => {
        this.context.commit(Mutations.RESET_NEWSLETTERS_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.UPDATE_NEWSLETTERS_CURRENT_PAGE](val) {
    this.context.commit(Mutations.SET_NEWSLETTERS_CURRENT_PAGE, val);
  }

  @Action
  [Actions.GET_NEWSLETTER](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.get('/newsletter-sections', id);
  }

  // @Action
  // [Actions.GET_NEWSLETTER_PREVIEW]() {
  //   ApiService.setAuthorizationHeader();
  //   return ApiService.get('/newsletter-sections/sender/preview');
  // }

  @Action
  [Actions.GET_NEWSLETTER_PREVIEW](version) {
    ApiService.setAuthorizationHeader();
    const params = { version };
    return ApiService.query('/newsletter-sections/sender/preview', { params });
  }

  @Action
  [Actions.GET_ARCHIVED_NEWSLETTER_PREVIEW](releaseIdentifier) {
    ApiService.setAuthorizationHeader();
    return ApiService.get(
      '/newsletter-sections/sender/preview-archived-newsletter',
      releaseIdentifier
    );
  }

  @Action
  [Actions.ARCHIVE_NEWSLETTER](payload) {
    ApiService.setAuthorizationHeader();
    return ApiService.post('/newsletter-sections/sender/archive', payload);
  }

  @Action
  [Actions.APPROVE_NEWSLETTER](payload) {
    ApiService.setAuthorizationHeader();
    return ApiService.post('/newsletter-bulk-push/approve', payload);
  }

  @Action
  [Actions.SEND_TO_SUBSCRIBERS](payload) {
    ApiService.setAuthorizationHeader();
    return ApiService.post('/newsletter-bulk-push/bulk-push', payload);
  }

  @Action
  [Actions.GET_NEWSLETTER_SUBSCRIBERS](filters) {
    ApiService.setAuthorizationHeader();
    const params = { page: this.currentPage, limit: 10, email: filters?.email };
    return ApiService.query('/newsletter-subscribers', { params })
      .then(({ data }) => {
        this.context.commit(Mutations.SET_NEWSLETTER_SUBSCRIBERS, data);
        return data.data;
      })
      .catch(({ response }) => {
        console.error(response);
        // this.context.commit(Mutations.SET_ERROR, {
        //   [response.statusText]: [response.data.message],
        // });
      });
  }

  @Action
  [Actions.UPDATE_NEWSLETTER_SUBSCRIBERS_CURRENT_PAGE](val) {
    this.context.commit(Mutations.SET_NEWSLETTER_SUBSCRIBERS_CURRENT_PAGE, val);
  }

  @Action
  [Actions.GET_NEWSLETTERS]() {
    ApiService.setAuthorizationHeader();
    return ApiService.query('/newsletter-sections', {
      params: { page: this.currentPage },
    })
      .then(({ data }) => {
        this.context.commit(Mutations.SET_NEWSLETTERS, data);
      })
      .catch(({ response }) => {
        console.error(response);
        // this.context.commit(Mutations.SET_ERROR, {
        //   [response.statusText]: [response.data.message],
        // });
      });
  }

  @Action
  [Actions.GET_ALL_NEWSLETTERS]() {
    ApiService.setAuthorizationHeader();
    return ApiService.get('/newsletter-sections/all')
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ALL_NEWSLETTERS, data);
        return data.data;
      })
      .catch(({ response }) => {
        console.error(response);
        // this.context.commit(Mutations.SET_ERROR, {
        //   [response.statusText]: [response.data.message],
        // });
      });
  }

  @Action
  [Actions.DELETE_NEWSLETTER_SUBSCRIBER](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.delete('/newsletter-subscribers', id)
      .then(() => {
        this.context.commit(Mutations.RESET_NEWSLETTERS_SUBSCRIBERS_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.UPLOAD_HERO_BANNER_IMAGE]({ id, data }) {
    ApiService.setAuthorizationHeader();
    ApiService.setMultipartHeader();
    return ApiService.post(`/newsletter-sections/uploadHeroBanner/${id}`, data)
      .then(({ data }) => {
        return data.data;
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }
}
